/**
 * En-tête « Ma maîtrise » de la page d'une SKILLCARD, AU-DESSUS de la carte et
 * sans cadre : HORA (gros chiffre + jauge + reste à gagner) · avatar et son aura
 * · maîtrise (% + statut). Les chiffres et la jauge prennent la teinte de la carte.
 */

import type { CSSProperties } from 'react';
import { Signal, SignalHigh, SignalLow, SignalMedium } from 'lucide-react';
import {
  CARD_MASTERY_STATUS_LABELS,
  cardMasteryStatus,
  horaRemaining,
  type CardMasteryStatus,
} from '@jeraisonne/db/cards';
import { AvatarCircle } from '@jeraisonne/ui';
import { AvatarAura } from './avatar-aura/avatar-aura';
import { useCountUp, useEntered } from './use-count-up';
import { HelpTip } from '@/components/help-tip';
import type { HoraStatus } from '@jeraisonne/db/cards';
import { HoraTrendBadge } from './hora-trend-badge';

/** Rythme de l'entrée (ms) : colonnes, puis chiffres + jauge, aura qui monte en puissance, statut. */
const COUNT_DELAY = 250;
const COUNT_DURATION = 1400;
const AURA_DELAY = 350;
const AURA_DURATION = 1800;

const STATUS_ICONS: Record<CardMasteryStatus, typeof Signal> = {
  discover: Signal,
  work: SignalLow,
  consolidate: SignalMedium,
  mastered: SignalHigh,
};

export function MasteryHero({
  xp,
  xpMax,
  mastery,
  used,
  name,
  avatarUrl,
  auraPower,
  accent,
  hora,
}: {
  xp: number;
  xpMax: number;
  mastery: number;
  /** L'élève a déjà répondu à une question reliée. */
  used: boolean;
  name: string;
  avatarUrl: string | null | undefined;
  /** Puissance de l'aura 0..100 (`cardAuraScore`). */
  auraPower: number;
  /** Teinte de la carte (hex). */
  accent: string;
  /** HORA vivant (migration 0315) : indicateur stable / en baisse sous le chiffre. */
  hora?: HoraStatus | null;
}) {
  // Entrée : les chiffres comptent depuis 0, la jauge se remplit, les effets
  // monte en puissance depuis 0 (l'aperçu admin suit en douceur).
  const entered = useEntered();
  const shownXp = Math.round(useCountUp(xp, { duration: COUNT_DURATION, delay: COUNT_DELAY }));
  const shownMastery = Math.round(
    useCountUp(mastery, { duration: COUNT_DURATION, delay: COUNT_DELAY }),
  );
  const shownPower = useCountUp(auraPower, { duration: AURA_DURATION, delay: AURA_DELAY });

  const pct = xpMax > 0 ? Math.max(0, Math.min(100, Math.round((xp / xpMax) * 100))) : 0;
  const remaining = horaRemaining(xp, xpMax);
  const status = cardMasteryStatus(mastery, used);
  const StatusIcon = STATUS_ICONS[status];
  // Teinte de la carte en variable CSS (seule valeur non statique).
  const vars = { '--jr-m-accent': accent } as CSSProperties;

  return (
    <section
      aria-label="Ma maîtrise"
      style={vars}
      className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-1 sm:px-16"
    >
      <div className="jr-hero-in relative z-10 min-w-0 space-y-1.5">
        <p className="flex items-center text-xs font-bold uppercase tracking-wider text-[var(--jr-m-accent)]">
          HORA
          {/* Aide contextuelle « C'est quoi l'HORA ? » (FAQ, clé `hora`), en exposant. */}
          <HelpTip topic="hora" label="HORA" superscript />
        </p>
        {/* Chiffre et maximum sur UNE ligne (le max, plus petit, ne passe jamais dessous). */}
        <p className="font-display flex items-baseline gap-x-1 whitespace-nowrap leading-none">
          <span className="text-3xl font-extrabold text-[var(--jr-m-accent)] sm:text-4xl">
            {shownXp}
          </span>
          <span className="text-ink-600 text-xs font-bold sm:text-lg">/ {xpMax}</span>
        </p>
        {hora && <HoraTrendBadge status={hora} />}
        {/* Même hauteur que le statut de maîtrise : les deux colonnes s'alignent. */}
        <div className="flex h-8 items-center">
          <div
            role="progressbar"
            aria-label="HORA de la carte"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-valuetext={
              remaining > 0
                ? `Encore ${remaining} HORA pour atteindre le maximum`
                : 'HORA max atteint'
            }
            className="h-3.5 w-full overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--jr-m-accent)_28%,transparent)]"
          >
            {/* Largeur dynamique : seule valeur non exprimable en classe statique. */}
            <div
              className="h-full rounded-full bg-[var(--jr-m-accent)] transition-[width] duration-[1400ms] ease-out motion-reduce:transition-none"
              style={{ width: `${entered ? pct : 0}%` }}
            />
          </div>
        </div>
      </div>

      <AvatarAura power={shownPower} color={accent} className="jr-hero-pop -mx-5 -my-4 sm:mx-0">
        <AvatarCircle name={name} src={avatarUrl} size="hero" className="shadow-lg" />
      </AvatarAura>

      <div className="jr-hero-in jr-hero-d1 relative z-10 min-w-0 space-y-1.5 text-right">
        <p className="text-ink-600 text-xs font-bold uppercase tracking-wider">Maîtrise</p>
        <p className="font-display text-3xl font-extrabold leading-none text-[var(--jr-m-accent)] sm:text-4xl">
          {shownMastery} <span className="text-xl sm:text-2xl">%</span>
        </p>
        <p
          data-status={status}
          className="jr-hero-in jr-hero-d2 text-ink-900 inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-xl border border-[color-mix(in_srgb,var(--jr-m-accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--jr-m-accent)_12%,transparent)] px-2 text-xs font-bold sm:gap-1.5 sm:px-2.5"
        >
          <StatusIcon className="h-4 w-4 shrink-0 text-[var(--jr-m-accent)]" aria-hidden />
          {CARD_MASTERY_STATUS_LABELS[status]}
        </p>
      </div>
    </section>
  );
}
